import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronDown, MessageCircle, BookOpen, Sparkles, ArrowUp } from "lucide-react";
import { CharacterChat } from "@/components/CharacterChat";

export default function Reels() {
  const [slides, setSlides] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    api.get("/reels").then(r => setSlides(r.data)).catch(() => setSlides([]));
  }, []);

  // Track which slide is in view
  useEffect(() => {
    if (!containerRef.current || slides.length === 0) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.6) {
          const idx = parseInt(e.target.getAttribute("data-idx") || "0", 10);
          setActiveIdx(idx);
        }
      });
    }, { root: containerRef.current, threshold: [0.6, 0.9] });
    const nodes = containerRef.current.querySelectorAll("[data-idx]");
    nodes.forEach(n => observerRef.current.observe(n));
    return () => observerRef.current?.disconnect();
  }, [slides]);

  if (slides.length === 0) {
    return <div className="min-h-[70vh] flex items-center justify-center text-slate-500">Loading reels…</div>;
  }

  const activeStory = slides[activeIdx];

  return (
    <div className="relative">
      <div
        ref={containerRef}
        data-testid="reels-container"
        className="h-[calc(100vh-4rem)] overflow-y-auto snap-y snap-mandatory scroll-smooth no-scrollbar"
      >
        {slides.map((s, i) => (
          <Slide key={s.story_id} slide={s} idx={i} active={i === activeIdx} />
        ))}
      </div>
      {activeStory && (
        <div className="fixed top-24 right-4 z-30 hidden md:block">
          <CharacterChat story={{ ...activeStory, chapters: [] }} />
        </div>
      )}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
        <div className="animate-bounce text-slate-400/60">
          <ArrowUp className="h-4 w-4 rotate-180" />
        </div>
      </div>
    </div>
  );
}

const Slide = ({ slide, idx, active }) => {
  const prot = slide.protagonist || {};
  return (
    <section
      data-idx={idx}
      data-testid={`reel-${slide.story_id}`}
      className="relative h-[calc(100vh-4rem)] w-full snap-start overflow-hidden flex items-end"
    >
      {slide.cover_url ? (
        <img
          src={slide.cover_url}
          alt={slide.title}
          className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[8000ms] ease-linear ${active ? "scale-110 translate-y-[-3%]" : "scale-100"}`}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-amber-950" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#07080A] via-[#07080A]/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#07080A]/70 via-transparent to-transparent md:from-[#07080A]/50" />

      <div className="relative z-10 w-full max-w-3xl mx-auto px-6 pb-16 md:pb-24 fade-in">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] px-2 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300">
            {slide.genre}
          </span>
          {slide.is_featured && (
            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400 flex items-center gap-1">
              <Sparkles className="h-3 w-3" /> Featured
            </span>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold leading-[1.02] tracking-tight text-white text-balance">
          {slide.title}
        </h1>
        <p className="mt-4 text-slate-300 text-sm md:text-base max-w-xl">{slide.tagline}</p>

        {prot.name && (
          <div className="mt-6 flex items-center gap-3 max-w-md">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-rose-500 text-black font-bold flex items-center justify-center flex-shrink-0">
              {prot.name.slice(0, 1)}
            </div>
            <div className="min-w-0">
              <div className="text-xs text-slate-400">
                <span className="font-mono uppercase tracking-widest text-amber-400">{prot.name}</span>
                <span className="text-slate-500"> · says</span>
              </div>
              <div className="font-serif italic text-slate-200 text-sm truncate">"{(slide.first_line || slide.tagline).slice(0, 120)}…"</div>
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to={`/story/${slide.story_id}`}
            data-testid={`reel-read-${slide.story_id}`}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11 px-5 text-sm"
          >
            <BookOpen className="h-4 w-4" /> Read the story
          </Link>
          <div className="md:hidden">
            <CharacterChat story={{ ...slide, chapters: [] }} />
          </div>
          <span className="text-xs text-slate-500">by {slide.author_name}</span>
        </div>
      </div>

      <div className="absolute bottom-4 right-6 z-10 flex flex-col items-center text-slate-400/60 pointer-events-none">
        <ChevronDown className="h-5 w-5" />
        <span className="font-mono text-[9px] uppercase tracking-widest mt-1">Scroll</span>
      </div>
    </section>
  );
};
